import { focusTargetById } from '@vs/components/btn-skip-to-main';
import { mainBlockID } from '@vs/ui/portal/layout';
import {
  MenuItemInterface,
  PortalMenu,
  UiPortalMenuProps,
} from '@vs/ui/portal/menu';
import { useMediaQuery } from '@vs/utils/mui';
import { FocusTrap } from 'focus-trap-react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useParentMenuItemOfMatchedChild } from './hooks';
import { MenuMeta, context } from './spa-path-config-provider';

type PropsSpaMenuByPathsConfig = {
  expand?: boolean;
  whiteListPolicies?: string[];
  onEsc?: () => void;
} & Pick<UiPortalMenuProps, 'onClick'>;

export function SpaMenuByPathsConfig({
  expand = false,
  onClick,
  onEsc,
  whiteListPolicies = [],
}: PropsSpaMenuByPathsConfig) {
  const { isMobile, isGreaterThanLg } = useMediaQuery();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { configs: pathsConfig } = useContext(context);
  const isFirstLevelPath = useParentMenuItemOfMatchedChild() === undefined;

  const menuItemPaths = pathsConfig
    .filter(p => p.menuMeta !== undefined && !p.hide)
    .toSorted((a, b) => (a.menuMeta?.order ?? 0) - (b.menuMeta?.order ?? 0));
  const menuItems = menuItemPaths.map((m): MenuItemInterface => {
    const menuMeta = m.menuMeta as MenuMeta;
    const disabled =
      whiteListPolicies.length > 0 &&
      m.menuMeta?.policyString !== undefined &&
      !whiteListPolicies.includes(m.menuMeta.policyString);
    const icon = menuMeta.Icon ? (
      <menuMeta.Icon className="me-2 text-2xl" />
    ) : null;
    if (m.type === 'link') {
      return {
        name: t(menuMeta.labelI18n),
        icon,
        url: m.href,
        disabled,
      };
    } else {
      return {
        name: t(menuMeta.labelI18n),
        icon,
        onClick: () => {
          navigate({
            ...location,
            pathname: m.path,
          });
          focusTargetById({ id: mainBlockID });
        },
        selected: location.pathname.startsWith(m.path),
        disabled,
      };
    }
  });
  const shouldRenderMenu = (() => {
    if (isMobile) {
      return expand;
    } else if (!isGreaterThanLg) {
      return isFirstLevelPath || expand;
    } else {
      return isFirstLevelPath;
    }
  })();
  const focusTrapActive = expand && !isGreaterThanLg;
  return (
    shouldRenderMenu && (
      <FocusTrap
        active={focusTrapActive}
        focusTrapOptions={{
          allowOutsideClick: true,
          escapeDeactivates: false,
          returnFocusOnDeactivate: true,
          initialFocus: false,
        }}
      >
        <div
          onKeyDown={e => {
            if (focusTrapActive && e.key === 'Escape' && onEsc) {
              onEsc();
            }
          }}
        >
          <PortalMenu
            isAppMenu
            menuItemList={menuItems}
            open={expand}
            onClick={onClick}
          />
        </div>
      </FocusTrap>
    )
  );
}
